import React, { createContext, useContext, useReducer } from 'react';

const Ctx = createContext(null);

const shallowEqual = (a = {}, b = {}) => {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  return ka.length === kb.length && ka.every(k => a[k] === b[k]);
};

const initialState = {
  location: { province: '', region: '', hdd: null },
  resultsByCalc: {}, // { [calcId]: { results, meta } }
  inputsByCalc: {}, // { [calcId]: { ...all input fields } }
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOCATION':
      return { ...state, location: action.payload };
    case 'SET_RESULTS': {
      const { calcId, results, meta } = action.payload;
      return { ...state, resultsByCalc: { ...state.resultsByCalc, [calcId]: { results, meta } } };
    }
    case 'RESET_RESULTS':
      return { ...state, resultsByCalc: {} };
    case 'SET_INPUTS': {
      const { calcId, inputs } = action.payload;
      const prev = state.inputsByCalc[calcId] || {};

      // If inputs are provided and nothing changed, skip
      if (inputs && shallowEqual(prev, inputs)) return state;

      if (inputs) {
        // merge/update inputs for this calcId
        return {
          ...state,
          inputsByCalc: {
            ...state.inputsByCalc,
            [calcId]: { ...prev, ...inputs },
          },
        };
      }

      // --- inputs is null/undefined → remove this calcId completely ---
      const rest = { ...state.inputsByCalc }; // copy existing inputs
      delete rest[calcId]; // remove the key
      return {
        ...state,
        inputsByCalc: rest,
      };
    }
    default:
      return state;
  }
}

// eslint-disable-next-line react/prop-types
export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const setLocation = React.useCallback(payload => dispatch({ type: 'SET_LOCATION', payload }), []);
  const setResults = React.useCallback(payload => dispatch({ type: 'SET_RESULTS', payload }), []);
  const resetResults = React.useCallback(() => dispatch({ type: 'RESET_RESULTS' }), []);
  const setInputs = React.useCallback(payload => dispatch({ type: 'SET_INPUTS', payload }), []);
  const value = React.useMemo(() => ({ state, setLocation, setResults, resetResults, setInputs }), [state]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useTRC = () => useContext(Ctx);
